import Reveal from "reveal.js/js/reveal";
import { UsageTracker } from "@visualizer/common/services/usageTracker/usageTracker";
import services from "../../services/glue/appGlue";

const presenter = {
  bindings: {
    boardsRow: "<",
    boardModels: "<",
    storyboardModel: "<",
    globalFilters: "<",
    onLoadMoreData: "&",
  },
  controller: PresenterController,
  controllerAs: "presenter",
  template: `
    <div class="reveal">
        <keyboard-shortcut class="keyboardButton" re-cb-on-click="presenter.toggleKeyboardHelp()"></keyboard-shortcut>   
        <div class="slides">
        <section class="center cover-slide">
          <div class="storyboard-title">
            {{presenter.storyboardTitle}}
          </div>
        </section>
        <section ng-repeat="board in presenter.flattenedBoards" class="{{board.type || 'visualization'}} displayShapes" data-board-id="{{board.id || 'unknown'}}">
          <!-- Use React component if feature flag is enabled, otherwise use Angular component -->
          <storyboard-board
            ng-if="presenter.isBoardVisible(board) && presenter.useReactLayout"
            re-in-board="board"
            re-in-edit-mode="false"
            re-in-font-zoom-ratio="presenter.zoomRatio"
            re-in-global-filters="presenter.globalFilters"
            re-in-storyboard-model="presenter.storyboardModel"
            re-in-board-model="presenter.boardModels[board.id]"
            re-cb-on-load-more-data="presenter.handleLoadMoredata(board)"
            re-in-presentation-mode="true"
          ></storyboard-board>
          <acl-storyboard-board
            ng-if="presenter.isBoardVisible(board) && !presenter.useReactLayout"
            board="board"
            board-model="presenter.boardModels[board.id]"
            can-drilldown="false"
            edit-mode="false"
            font-zoom-ratio="presenter.zoomRatio"
            global-filters="presenter.globalFilters"
            on-load-more-data="presenter.handleLoadMoredata(board)"
            presentation-mode="true"
            storyboard-model="presenter.storyboardModel"
          ></acl-storyboard-board>
        </section>
      </div>
    </div>
  `,
  restrict: "E",
};

function PresenterController($element, $scope, $timeout) {
  "ngInject";
  const self = this;

  const RENDERED_BOARD_BUFFER = 5;

  let boardsToRender = [];
  let isUsingTransformScale = false;
  let isOverviewMode = false;
  let currentSlideIndex = 0;

  // Determine whether to use React or Angular layout based on feature flag
  self.useReactLayout = services && services.AppConfig && services.AppConfig.features && services.AppConfig.features.enableStoryboardReactLayoutComponent;

  $scope.$watch(getZoomRatio, zoomRatio => updateBoardFontZoomRatio(zoomRatio));

  self.zoomRatio = 1;

  self.$onChanges = changesObj => {
    if (changesObj.boardsRow) {
      self.flattenedBoards = changesObj.boardsRow.currentValue.reduce(
        (flattenedBoards, currBoards) => {
          // Validate and sanitize board data to prevent undefined values
          const boards = currBoards.boards || [];
          const validBoards = boards.map((board, index) => {
            if (!board.id) {
              console.error('Presenter: Board missing ID, this indicates a data integrity issue:', board);
              const fallbackId = `missing_id_${currBoards.row || 0}_${index}_${board.type || 'unknown'}`;
              console.warn(`Presenter: Using fallback ID: ${fallbackId}`);
            }
            
            // Ensure all required properties exist with fallback values
            const validatedBoard = {
              ...board,
              id: board.id || `missing_id_${currBoards.row || 0}_${index}_${board.type || 'unknown'}`,
              type: board.type || 'visualization',
              title: board.title || '',
              text: board.text || ''
            };
            return validatedBoard;
          });
          return flattenedBoards.concat(validBoards);
        },
        []
      );
    }
    if (changesObj.storyboardModel) {
      const model = changesObj.storyboardModel.currentValue;
      self.storyboardTitle = (model && typeof model.getName === 'function' && model.getName()) || 'Untitled Storyboard';
    }
    if (changesObj.boardModels) {
      self.boardModels = changesObj.boardModels.currentValue;
    }
    initReveal();
  };

  self.isBoardVisible = board => {
    const isVisible = boardsToRender.includes(board.id);
    return isVisible;
  };

  self.handleLoadMoredata = board => {
    self.onLoadMoreData({ board: board });
  };

  self.toggleKeyboardHelp = () => {
    Reveal.toggleHelp();
  };

  function initReveal() {
    window.Reveal = Reveal; // expose for post-init configuration
    $timeout(() => {
      if (!Reveal.getCurrentSlide()) {
        Reveal.initialize({
          controlsTutorial: false,
          dependencies: [],
        });
        Reveal.addEventListener("ready", () => {
          addNoZoomOrTransformClass();
          updateBoardsToShow(0);
          Reveal.slide(0);
          UsageTracker.createEvent(`.loaded`);
        });

        Reveal.addEventListener("slidechanged", event => {
          trackSlideChanged(event.indexh);
          updateBoardsToShow(event.indexh);
          currentSlideIndex = event.indexh;
        });
        Reveal.addEventListener("overviewshown", () => {
          isOverviewMode = true;
          trackOverviewChanged();
          $scope.$apply();
        });
        Reveal.addEventListener("overviewhidden", () => {
          isOverviewMode = false;
          trackOverviewChanged();
          $scope.$apply();
        });

        document.addEventListener("keydown", event => {
          if (event.ctrlKey && event.keyCode === 191) {
            Reveal.toggleHelp();
          }
        });
      }
    });
  }

  function addNoZoomOrTransformClass() {
    if (isUsingTransformScale) {
      $element.find(".slides").addClass("no-transform-scale");
      $element.find(".slides").removeClass("no-zoom");
    } else {
      $element.find(".slides").addClass("no-zoom");
      $element.find(".slides").removeClass("no-transform-scale");
    }
  }

  function updateBoardFontZoomRatio(zoomRatio) {
    if (zoomRatio) {
      self.zoomRatio = zoomRatio;
    }
  }

  function getZoomRatio() {
    if (isOverviewMode) return;

    let elementStyle = $element.find(".slides").attr("style");
    if (elementStyle) {
      let elementStyleList = elementStyle.split(";");
      let transformCss = elementStyleList.find(cssStyle => cssStyle.indexOf("transform:") > -1);
      let zoomCss = elementStyleList.find(cssStyle => cssStyle.indexOf("zoom:") > -1);
      if (transformCss) {
        isUsingTransformScale = true;
        return Number.parseFloat(transformCss.match(/scale\((.*)\)$/)[1]);
      }
      if (zoomCss) {
        isUsingTransformScale = false;
        return Number.parseFloat(zoomCss.split(":")[1].trim());
      }
    }
  }

  function updateBoardsToShow(currentIndex) {
    boardsToRender = [];
    let startBoardIndex = Math.max(currentIndex - RENDERED_BOARD_BUFFER, 0);
    let endBoardIndex = Math.min(currentIndex + RENDERED_BOARD_BUFFER, self.flattenedBoards.length);
    boardsToRender = self.flattenedBoards.slice(startBoardIndex, endBoardIndex).map(board => board.id);
    $scope.$apply();
  }

  function trackOverviewChanged() {
    let overviewState = isOverviewMode ? ".opened" : ".exited";
    UsageTracker.createEvent(`.overview${overviewState}`);
  }

  function trackSlideChanged(slideIndex) {
    let direction = getDirection(slideIndex);
    let overviewFragment = isOverviewMode ? ".overview" : "";
    if (direction) {
      UsageTracker.createEvent(`${overviewFragment}${direction}`);
    }
  }

  function getDirection(slideIndex) {
    switch (slideIndex - currentSlideIndex) {
      case 1:
        return ".forward";
      case -1:
        return ".backward";
    }
  }
}

export default presenter;
